import type {RefObject} from 'react';
import {useEffect, useRef, useState} from 'react';

/**
 * /workstreams: while the pointer is level with `ref`'s element —
 * inside it, or out in the page's margins either side of it (the nearest
 * `[data-pointer-band-root]`'s width) — returns `resolve(pointerY)`; otherwise
 * undefined. Re-renders only when that result changes, and follows scrolling
 * under a still pointer.
 */
export const usePointerBand = <T>(
  ref: RefObject<HTMLElement | null>,
  resolve: (pointerY: number) => T
): T | undefined => {
  const [result, setResult] = useState<T | undefined>(undefined);
  const resolveRef = useRef(resolve);
  resolveRef.current = resolve;

  useEffect(() => {
    let pointer: {x: number; y: number} | null = null;
    const update = () => {
      const element = ref.current;
      if (!element || !pointer) {
        setResult(undefined);
        return;
      }
      const {top, bottom} = element.getBoundingClientRect();
      const {left, right} = (
        element.closest('[data-pointer-band-root]') ?? element
      ).getBoundingClientRect();
      const {x, y} = pointer;
      setResult(
        y >= top && y < bottom && x >= left && x < right ? resolveRef.current(y) : undefined
      );
    };
    const handleMove = (event: PointerEvent) => {
      pointer = {x: event.clientX, y: event.clientY};
      update();
    };
    const handleLeave = () => {
      pointer = null;
      update();
    };
    document.addEventListener('pointermove', handleMove, {passive: true});
    document.documentElement.addEventListener('pointerleave', handleLeave);
    window.addEventListener('scroll', update, {capture: true, passive: true});
    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.documentElement.removeEventListener('pointerleave', handleLeave);
      window.removeEventListener('scroll', update, {capture: true});
    };
  }, [ref]);

  return result;
};
